import appVersion from '@baseDir/appVersion.json';
import { i18n } from '@boot/i18n.js';
import Axios from 'axios';
import axiosRetry from 'axios-retry';
import { Notify, Platform } from 'quasar';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import {
    isDeprecatedDomain,
    performDeprecatedDomainCleanup
} from '@utils/tenantValidation.js';

// initialize base URL
let baseURL = null;

if (Platform.is.capacitor) {
    // on mobile, the user has to choose the name of his tenant
    // account on the login form. After validation, the tenant name
    // is added to he store and the base URL is set here.
    const tenantURL = localStorage.getItem('tenantURL');

    if (tenantURL) {
        // Enhanced cleanup logic using centralized validation
        // This now properly handles race conditions and provides better error handling
        if (isDeprecatedDomain(tenantURL)) {
            console.warn(
                'Deprecated domain detected in axios boot:',
                tenantURL
            );

            // Perform comprehensive cleanup
            performDeprecatedDomainCleanup();

            // Reload the app to ensure all state is reset
            window.location.reload();
        } else {
            baseURL = tenantURL;
        }
    }
} else {
    // on desktop, the tenant url is specified in ENV variable
    if (process.env.TENANT_URL) {
        // if a API url is defined in a local .env file, use it
        baseURL = process.env.TENANT_URL + '/v1.1/api/';
    } else {
        if (process.env.DEV) {
            console.warn(
                'No `TENANT_URL` has been set while running in development mode and this application will not communicate properly with the API. To resolve this, specify the `TENANT_URL` settings in your `.env` file as described in the SETUP.md instructions'
            );
        } else {
            // otherwise, use relative url from the current host
            // this is useful in production when the frontend and the backend are hosted
            // on the same server
            baseURL =
                window.location.protocol +
                '//' +
                window.location.host +
                '/v1.1/api/';

            // Enhanced desktop cleanup - now performs automatic cleanup like mobile
            if (isDeprecatedDomain(baseURL)) {
                console.warn('Deprecated domain detected on desktop:', baseURL);
                performDeprecatedDomainCleanup();

                // Redirect to a safe domain or show error page
                window.location.href = '/login';
            }
        }
    }
}

console.debug('Starting application with baseUrl=', baseURL);

// create axios instance to be used globally in the application
const axios = Axios.create({
    baseURL: baseURL
});

// Set the default number of retries to 0
// This package was introduced for the retry logic for the new push notification system
// See @boot/capacitorPlugins.js for more details. In the future, we may want to
// allow retries for other requests as well.
axiosRetry(axios, { retries: 3 });

// set default content type
axios.defaults.headers.common['Content-Type'] = 'application/json';
// include frontend version in the request header, then on the backend this
// information will be added to Sentry error reports as a tag to help with debugging
axios.defaults.headers.common['FRONTEND-VERSION'] = appVersion.version;

axios.defaults.timeout = 30000; // 30 seconds

axios.defaults.timeoutErrorMessage = 'timeout';

export default async ({ router, app }) => {
    /**
     * Handle when a user's session has expired.
     */
    function handleSessionExpired() {
        useAuthStore().logoutAction();

        // Set a notification popup to inform the user that their session has expired
        useSharedStore().setNotification({
            type: 'error',
            message: i18n.global.t(
                'Your session has expired. Please log in again to continue.'
            )
        });

        router.push({ name: 'login' });
    }

    /**
     * Handles the case when user account is removed.
     */
    function handleRemovedUser() {
        useAuthStore().logoutAction();

        // Set a notification popup to inform the user that their account has removed
        useSharedStore().setNotification(
            {
                type: 'error',
                message: i18n.global.t(
                    'This account no longer exists. Please contact your account manager for further details.'
                )
            },
            null,
            { root: true }
        );

        router.push({ name: 'login' });
    }

    /**
     * Handles the case when user account is inactive.
     */
    function handleInactiveUser() {
        useAuthStore().logoutAction();

        // Set a notification popup to inform the user that their account has been disabled
        useSharedStore().setNotification(
            {
                type: 'error',
                message: i18n.global.t(
                    `This account is disabled. Please check your email inbox to activate your account or contact support at support{'@'}iotorq-lean.com.`
                )
            },
            null,
            { root: true }
        );

        router.push({ name: 'login' });
    }

    /**
     * Handles responses with a 403 status code.
     */
    function handlePermissionError() {
        useSharedStore().setNotification(
            {
                type: 'error',
                message: i18n.global.t(
                    'Your account does not have permission to perform this action.'
                )
            },
            null,
            { root: true }
        );
    }

    /**
     * Refreshes the access token using the refresh token.
     * @return {String} The new access token, if successful.
     */
    async function fetchAccessToken() {
        // Check if the refresh token is valid
        const hasValidRefresh =
            await useAuthStore().hasValidRefreshTokenAction();
        if (!hasValidRefresh) {
            return Promise.reject(new Error('Invalid refresh token'));
        }

        // get the refresh token from the store
        const refreshToken = useAuthStore().getRefreshToken;
        if (!refreshToken) {
            return Promise.reject(new Error('No refresh token available'));
        }

        try {
            let accessToken = null;
            // refresh the access token
            console.debug('Refresh JWT before request.');
            const response = await axios.post(
                'auth/jwt/refresh/',
                {
                    refresh: refreshToken
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    disableRequestInterceptor: true // / perform request without the interceptor to avoid infinite loop
                }
            );

            accessToken = response.data['access'];
            // save the new tokens
            useAuthStore().updateAccessTokenAction(accessToken);
            if (response.data['refresh']) {
                useAuthStore().updateRefreshTokenAction(
                    response.data['refresh']
                );
            }
            return accessToken;
        } catch (error) {
            console.error(`Failed to refresh access token due to ${error}`);

            // Reject the promise with the error such that the interceptor can react to the error accordingly
            return Promise.reject(error);
        }
    }

    /**
     * Obtain access token from store, or fetch a new one if needed.
     * @param {String}
     */
    async function getAccessTokenOrFetch() {
        // use access token if we have it
        let accessToken = useAuthStore().getAccessToken;
        const hasValidAccessToken =
            await useAuthStore().hasValidAccessTokenAction();
        // check if we need to refresh the access token
        if (!hasValidAccessToken) {
            try {
                // refresh the access token
                accessToken = await fetchAccessToken();
            } catch (error) {
                console.error(`Failed to refresh access token due to ${error}`);
            }
        }

        return accessToken;
    }

    // axios interceptors
    axios.interceptors.request.use(
        async request => {
            // If disableRequestInterceptor is set to true, then do not handle the request
            if (request.disableRequestInterceptor === true) {
                return Promise.resolve(request);
            }
            // get access token from store (or server if needed)
            let accessToken = null;
            try {
                accessToken = await getAccessTokenOrFetch();
            } catch (error) {
                return Promise.reject(
                    new Error(
                        `Unable to refresh access token for request due to token error ${error}`
                    )
                );
            }

            // add token to headers
            if (accessToken) {
                // update request headers
                // After updating Axios to 1.6.2: do not set the
                // Authorization inside common headers, like this:
                // request.headers.common['Authorization']
                request.headers['Authorization'] = `Bearer ${accessToken}`;
            }
            return Promise.resolve(request);
        },
        error => {
            return Promise.reject(new Error(error));
        }
    );

    axios.interceptors.response.use(
        // if status code 2XX
        response => {
            return response;
        },
        async error => {
            // If disableResponseInterceptor is set to true, then do not handle the error
            if (
                error.config &&
                error.config.disableResponseInterceptor === true
            ) {
                return Promise.reject(new Error(error));
            }
            // --- TIMEOUT ERRORS ---
            if (error.code === 'ECONNABORTED') {
                const networkErrorMessage = i18n.global.t(
                    'Connection timed out. Please try again.'
                );

                Notify.create({
                    message: networkErrorMessage,
                    color: 'red',
                    textColor: 'white',
                    position: 'top'
                });

                console.error(networkErrorMessage);
                return Promise.reject(new Error('Request timed out'));
            }
            // --- NETWORK ERRORS ---
            else if (error.code === 'ERR_NETWORK') {
                let networkErrorMessage = '';
                // Browser has no internet connection
                if (!navigator.onLine) {
                    networkErrorMessage = i18n.global.t(
                        'You have lost network connectivity. Please check your network settings.'
                    );
                } else {
                    // Default to generic network error
                    networkErrorMessage = i18n.global.t(
                        'A network error occurred. Please check your network settings and try again.'
                    );
                }

                Notify.create({
                    message: networkErrorMessage,
                    color: 'red',
                    textColor: 'white',
                    position: 'top'
                });

                console.error(networkErrorMessage);
                return Promise.reject(new Error(networkErrorMessage));
            }

            // --- 4XX and 5XX ERRORS ---
            if ('response' in error && error.response) {
                const { status, data } = error.response;

                if (status === 401) {
                    // check if permission error due to deleted user accounts
                    if (data.code === 'user_not_found') {
                        // handle the remove
                        handleRemovedUser();
                    } else if (data.code === 'user_inactive') {
                        // handle inactive user for any request except login
                        // as a error banner is already shown on the login page
                        // See Login.vue for more details
                        if (error.config.url !== '/auth/login/') {
                            handleInactiveUser();
                        }
                    } else if (data.code === 'token_not_valid') {
                        // refresh token is expired, so end session
                        handleSessionExpired();
                    } else if (data.code === 'authentication_failed') {
                        // refresh token is expired, so end session
                        handleSessionExpired();
                    }
                } else if (status === 403) {
                    if (!('account_lockout' in data)) {
                        handlePermissionError();
                    }
                } else if (status === 500) {
                    // Ignore the error if it is a stream request or a justify event request as
                    // the app is still partially usable without these requests succeeding
                    if (
                        !error.config.url.includes('stream/') &&
                        !error.config.url.includes('events/resolve/') &&
                        !error.config.url.includes('quality_events/justify/') &&
                        // We also ignore planning optimization errors here since the service
                        // may be temporarily down but the rest of the app is still usable.
                        !error.config.url.includes('planning/optimize/')
                    ) {
                        // Internal server errors may break the app, so redirect to error page
                        router.push({
                            name: 'error',
                            state: { code: 'API_SERVER_ERROR' }
                        });
                    }
                }

                // Return axios error object. Therefore, the error must be handled
                // by the component that made the request
                return Promise.reject(error);
            }

            // fallback: reject the request
            return Promise.reject(error);
        }
    );
    app.config.globalProperties.$axios = axios;
};

export { axios, baseURL };

// Starting from clean state (no token in storage)
// 1. Login & logout
// 2. Normal AJAX works after Login
// 3. AJAX call with an expired access token but valid refresh token:
//     - axios request interceptor -> try to refresh the access token
//             - access token refresh ok -> AJAX call continue
//             - access token refresh failed -> exception raised (network issue?)
// 4. Getting out of sleep mode. No valid refresh token. AJAX calls is sent. Axios request interceptor does nothing.
//         NO AUTHORIZATION HEADER IS ADDED TO THE REQUEST (which results in 401 error: Authentication credentials were not provided.)
//         log the user out
